import { FC, useEffect, useMemo, useState } from "react";
import MapRoute from "../../../../../../common/components/map/types/map-route";
import MapMarker from "../../../../../../common/components/map/types/map-marker";
import { faWrench, faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ButtonComponent from "../../../../../../common/components/button/button.component";
import LinkButtonComponent from "../../../../../../common/components/button/link/link-button.component";
import MapComponent from "../../../../../../common/components/map/map.component";
import OrderOptimizerRoadRouteParams from "../../../../../../common/services/order-optimizer/road-route/order-optimizer-road-route-params";
import useOrderOptimizerRoadRoute from "../../../../../../common/services/order-optimizer/road-route/use-order-optimizer-road-route";
import { SearchRoadRoutingResponseRouteGeometryCoordinate } from "../../../../../../common/utils/search-road-route/search-road-routing.response";
import orderOptimizerTranslationsHelper from "../../../../../../languages/order-optimizer-translations.helper";
import orderOptimizerHelper from "../../../../common/order-optimizer.helper";
import orderOptimizerRoutesHelper from "../../../../common/routes/order-optimizer-routes.helper";
import orderOptimizerCargoListingMapMarkersFactory from "../../common/order-optimizer-cargo-listing-map-markers.factory";
import orderOptimizerCargoListingRouteFactory from "../../common/order-optimizer-cargo-listing-route.factory";
import orderOptimizerCargoListingWaypointFactory from "../../common/order-optimizer-cargo-listing-waypoint.factory";
import {
  OrderOptimizerCargoListingItem,
  OrderOptimizerCargoListingItemRoute,
} from "../../common/types/order-optimizer-cargo-listing-item";
import orderOptimizerCargoListingTableRowHelper from "./order-optimizer-cargo-listing-table-row.helper";

type OrderOptimizerCargoListingTableRowProps = {
  listingItem: OrderOptimizerCargoListingItem;
  selectedHumanId: number | undefined;
  onOptimizationRequestClick: () => void;
};

const OrderOptimizerCargoListingTableRowComponent: FC<
  OrderOptimizerCargoListingTableRowProps
> = (props) => {
  const [waypoints, setWaypoints] = useState<
    OrderOptimizerCargoListingItemRoute[]
  >([]);

  const [mapRoutes, setMapRoutes] = useState<MapRoute[]>([]);

  const roadRoute = useOrderOptimizerRoadRoute();

  const mapMarkers: MapMarker[] = useMemo(() => {
    const mapMarkers =
      orderOptimizerCargoListingMapMarkersFactory.createMapMarkers(waypoints);

    return mapMarkers;
  }, [waypoints]);

  const translations =
    orderOptimizerTranslationsHelper.getCargoListingTranslations().table;

  useEffect(() => {
    if (!props.listingItem) {
      return;
    }

    const preparedWaypoints =
      orderOptimizerCargoListingWaypointFactory.createWaypoints(
        props.listingItem,
      );

    setWaypoints(preparedWaypoints.flat());

    const loadParams: OrderOptimizerRoadRouteParams[] = [];

    preparedWaypoints.forEach((preparedWaypointsRoute) => {
      if (preparedWaypointsRoute.length > 1) {
        const request =
          orderOptimizerCargoListingWaypointFactory.createSearchRoutingRequest(
            preparedWaypointsRoute,
          );
        loadParams.push(request);
      }
    });

    roadRoute.load(loadParams);
  }, [props.listingItem]);

  useEffect(() => {
    if (!roadRoute.data) {
      return;
    }

    const routes = roadRoute.data.map((route, index) => {
      const formattedCoordinates: SearchRoadRoutingResponseRouteGeometryCoordinate[] =
        route.coordinates;

      return orderOptimizerCargoListingRouteFactory.createMapRoute(
        formattedCoordinates,
        orderOptimizerHelper.getOrderColor(index),
      );
    });

    setMapRoutes(routes);
  }, [roadRoute.data]);

  const scoreLabel = orderOptimizerCargoListingTableRowHelper.getScoreLabel(
    props.listingItem.score,
  );

  const idLabel = orderOptimizerCargoListingTableRowHelper.getIdLabel(
    Number(props.listingItem.id),
  );

  return (
    <div className="order_optimizer_cargo_table_row">
      <div className="order_optimizer_cargo_table_row__left">
        <MapComponent markers={mapMarkers} autoFit routes={mapRoutes} />
      </div>
      <div className="order_optimizer_cargo_table_row__right">
        <div className="order_optimizer_cargo_table_row__right__upper_section">
          <div className="order_optimizer_cargo_table_row__right__upper_section__left">
            <h1 className="order_optimizer_cargo_table_row__right__upper_section__text">
              {scoreLabel}
            </h1>
            <h1 className="order_optimizer_cargo_table_row__right__upper_section__text">
              {idLabel}
            </h1>
          </div>
          <div className="order_optimizer_cargo_table_row__right__upper_section__right">
            {props.listingItem.reviewedBy === null ? (
              <ButtonComponent
                type="primary"
                title={translations.optimizationButtonTitle}
                classNames={{
                  root: "order_optimizer_cargo_table_row__right__upper_section__right__button",
                }}
                onClick={props.onOptimizationRequestClick}
              >
                <div className="order_optimizer_cargo_table_row__text">
                  {translations.optimizationButtonLabel}
                  <FontAwesomeIcon
                    icon={faWrench}
                    className="order_optimizer_cargo_table_row__right__upper_section__right__button__icon"
                  />
                </div>
              </ButtonComponent>
            ) : (
              <>
                <LinkButtonComponent
                  to={orderOptimizerRoutesHelper.getDetailsRoute({
                    id: props.listingItem.id,
                  })}
                  type="primary"
                  title={translations.previewOptimizationButtonTitle}
                  classNames={{
                    root: "order_optimizer_cargo_table_row__right__upper_section__right__button_preview",
                  }}
                  openInNewTab
                >
                  <div className="order_optimizer_cargo_table_row__text">
                    {translations.previewOptimizationButtonLabel}
                    <FontAwesomeIcon
                      icon={faEye}
                      className="order_optimizer_cargo_table_row__right__upper_section__right__button_preview__icon"
                    />
                  </div>
                </LinkButtonComponent>
                <div
                  className="order_optimizer_cargo_table_row__right__upper_section__right__badge"
                  title={`${
                    props.listingItem.reviewedBy
                  }, ${orderOptimizerCargoListingTableRowHelper.getDateLabel(
                    props.listingItem.reviewedAt,
                  )}`}
                >
                  {`${
                    props.listingItem.reviewedBy
                  }, ${orderOptimizerCargoListingTableRowHelper.getDateLabel(
                    props.listingItem.reviewedAt,
                  )}`}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="order_optimizer_cargo_table_row__right__lower_section">
          <div className="order_optimizer_cargo_table_row__right__lower_section__left">
            <div className="order_optimizer_cargo_table_row__right__lower_section__left__item">
              <h3 className="order_optimizer_cargo_table_row__right__lower_section__left__item__text">
                {translations.orderNumbersLabel}
              </h3>
              {props.listingItem.orders.map((order) => (
                <div
                  key={order.humanId}
                  className={
                    props.selectedHumanId === order.humanId
                      ? "order_optimizer_cargo_table_row__right__lower_section__left__item__highlighted"
                      : "order_optimizer_cargo_table_row__right__lower_section"
                  }
                >
                  {order.humanId}
                </div>
              ))}
            </div>
            <div className="order_optimizer_cargo_table_row__right__lower_section__left__item">
              <h3 className="order_optimizer_cargo_table_row__right__lower_section__left__item__text">
                {translations.startDateLabel}
              </h3>
              {props.listingItem.orders.map((order) => (
                <div
                  key={order.humanId}
                  className="order_optimizer_cargo_table_row__right__lower_section"
                >
                  {orderOptimizerCargoListingTableRowHelper.getDateLabel(
                    order.startTime,
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderOptimizerCargoListingTableRowComponent;
